import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { useEnabledCalendars } from './hooks/useEnabledCalendars';
import { DayDataSlot } from './hooks/useDayData';
import { useGoogleCalendar } from './hooks/useGoogleCalendar';
import { useMeetingOptions } from './hooks/useMeetingOptions';
import { useDateNav } from './hooks/useDateNav';
import { useScheduleTheme } from './hooks/useScheduleTheme';
import { useSlotActions } from './hooks/useSlotActions';
import { useDailySummary } from './hooks/useDailySummary';
import { useEventsForView } from './hooks/useEventsForView';
import { useHomeTimezone } from './hooks/useHomeTimezone';
import { useCityRowOrdering } from './hooks/useCityRowOrdering';
import DailySummary from './components/DailySummary';
import MeetingOptionsPanel from './components/meeting-options/MeetingOptionsPanel';
import { ActionModal } from './components/schedule/ActionModal';
import { CityRow } from './components/schedule/CityRow';
import { DayCard } from './components/schedule/DayCard';
import { EventTooltip } from './components/schedule/EventTooltip';
import { ScheduleHeader } from './components/schedule/ScheduleHeader';
import { buildHourList } from './utils/slotMath';
import './App.css';

const FALLBACK_HOME_TZ = 'America/New_York';

export default function App({
  calendarRefreshKey,
  selectedTimezones = [],
  onRemoveCity,
  onReorderCities,
  onOpenCalendarPreferences,
  use24HourFormat = false,
}) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { enabledCalendars } = useEnabledCalendars();
  const { connected: gcalConnected } = useGoogleCalendar();
  const { t, i18n } = useTranslation();

  // Home timezone — right-clickable, persisted, kept in sync across / and /v2.
  const { homeCity, setHomeCity } = useHomeTimezone(selectedTimezones);
  const HOME_TZ = homeCity?.timezone || FALLBACK_HOME_TZ;

  const [now, setNow] = useState(() => DateTime.now().setZone(HOME_TZ));
  const {
    anchorDate, setAnchorDate,
    viewModeId, setViewModeId,
    dayCount, days,
    titleRange,
    goPrev, goNext, goToday,
  } = useDateNav(HOME_TZ, i18n.language);
  const [hoveredHour, setHoveredHour] = useState(null);
  const [pillTip, setPillTip] = useState(null);

  // Per-day event data, keyed by ISO date.
  const [dayEventData, setDayEventData] = useState({});
  const handleDayResult = useCallback((dateISO, result) => {
    setDayEventData(prev => {
      const cur = prev[dateISO];
      if (cur && cur.loading === result.loading && cur.error === result.error &&
          cur.eventsByCalendar === result.eventsByCalendar) {
        return prev;
      }
      return { ...prev, [dateISO]: result };
    });
  }, []);

  useEffect(() => {
    setNow(DateTime.now().setZone(HOME_TZ));
    const id = setInterval(() => setNow(DateTime.now().setZone(HOME_TZ)), 60_000);
    return () => clearInterval(id);
  }, [HOME_TZ]);

  // Theme switcher — six curated palettes. Picker UI lives in Header.tsx;
  // this just reads the persisted choice for the page-level data-theme.
  const [theme] = useScheduleTheme();

  const today = now.startOf('day');
  const currentHomeHour = now.hour;

  // City rows + drag-to-reorder state. Home pins to the top; in 'auto'
  // mode the rest sort by absolute offset to home, in 'manual' mode the
  // user's saved order is honoured.
  const {
    orderedCityRows,
    reorderActive,
    startReorder,
    finishReorder,
    handleRowDragStart,
    handleRowDragOver,
    handleRowDragEnd,
  } = useCityRowOrdering({ selectedTimezones, homeCity, HOME_TZ, onReorderCities, language: i18n.language });

  // enabledCalendars items have {id, name, color} (see api/utils/db.js:353
   // — `SELECT calendar_id as id, calendar_name as name, color`).
  const calendars = useMemo(() => {
    return (enabledCalendars || []).map((c, idx) => ({
      key: c.id || `cal-${idx}`,
      id: c.id,
      name: c.name || c.id,
      accent: c.color || '#60a5fa',
    }));
  }, [enabledCalendars]);

  // Per-day events-by-calendar + summary feed builder for the picked day.
  // Sequential numbering is shared between the schedule pills and the
  // daily summary so clicking a pill scrolls to the matching #event-N row.
  const {
    eventsByDayAndCal,
    allDayByDay,
    visibleISOs,
    defaultSummaryISO,
    buildAllEventsForSummary,
  } = useEventsForView({ days, dayEventData, today, HOME_TZ });

  // Two-pass dance: the daily-summary hook needs `allEventsForSummary` to
  // know whether to auto-open, but the events list depends on the
  // effective ISO the hook returns. We solve it by computing events from
  // the default ISO first (it's what the hook falls back to anyway) and
  // letting the hook pin the user-selected ISO via internal state.
  const provisionalEvents = useMemo(
    () => buildAllEventsForSummary(defaultSummaryISO),
    [buildAllEventsForSummary, defaultSummaryISO]
  );

  const summary = useDailySummary({
    allEventsForSummary: provisionalEvents,
    visibleISOs,
    defaultSummaryISO,
  });

  const allEventsForSummary = useMemo(
    () => buildAllEventsForSummary(summary.effectiveSummaryISO),
    [buildAllEventsForSummary, summary.effectiveSummaryISO]
  );

  const summaryDate = useMemo(
    () => DateTime.fromISO(summary.effectiveSummaryISO, { zone: HOME_TZ }),
    [summary.effectiveSummaryISO, HOME_TZ]
  );

  const hasMultipleCalendars = calendars.length > 1;

  const referenceDay = days.find(d => d.hasSame(today, 'day')) || days[0];
  const dayStartHomeRef = referenceDay.setZone(HOME_TZ).startOf('day');
  const isReferenceToday = referenceDay.hasSame(today, 'day');

  // Meeting options — shared hook ported from App.js.
  const meeting = useMeetingOptions({
    selectedCities: orderedCityRows.map(c => ({
      name: c.name,
      displayName: c.displayName,
      timezone: c.timezone,
      timezoneName: c.tzAbbr || '',
    })),
    isSupporter: false,
    use24HourFormat,
    getLocalizedCityName: (c) => c.displayName || c.name,
    i18nLanguage: i18n.language,
    t,
    defaultMeetingDuration: 60,
  });

  // Action modal for time-slot clicks (Add Meeting Option, Copy, Gmail,
  // Google Calendar, iCal — all operate on the slot the user just clicked).
  const slotActions = useSlotActions({
    orderedCityRows,
    addMeetingOption: meeting.addMeetingOption,
  });

  // Map dateISO -> Set<hour> for slots that already have a meeting option.
  // Used by the day cards to keep those calendar-row slots highlighted
  // until the user clears them via the meeting-options panel.
  const meetingPickByDate = useMemo(() => {
    const map = {};
    meeting.meetingOptions.forEach(opt => {
      const local = opt.startDateTime.setZone(HOME_TZ);
      const iso = local.toISODate();
      const hour = local.hour;
      if (!map[iso]) map[iso] = new Set();
      map[iso].add(hour);
    });
    return map;
  }, [meeting.meetingOptions, HOME_TZ]);

  // In multi-day views the slot height defaults to a more compact 26px to
  // fit dense days vertically. Bump it back to the Day-mode 32px when the
  // user only has a handful of cities so each row stays comfortable.
  const pageStyle = (dayCount > 1 && orderedCityRows.length <= 3)
    ? { '--wv-slot-h': '32px' }
    : undefined;

  return (
    <div
      className={`wv-page wv-page--v2 wv-page--${viewModeId}`}
      data-theme={theme}
      style={pageStyle}
    >
      {/* Headless event loaders — one per visible day */}
      {!authLoading && isAuthenticated && days.map(d => (
        <DayDataSlot
          key={d.toISODate()}
          date={d}
          refreshKey={calendarRefreshKey}
          onResult={handleDayResult}
        />
      ))}

      <ScheduleHeader
        titleRange={titleRange}
        selectedDate={anchorDate.toJSDate()}
        onSelectDate={(date) => {
          const dt = DateTime.fromJSDate(date).setZone(HOME_TZ).startOf('day');
          setAnchorDate(dt);
        }}
        viewModeId={viewModeId}
        onChangeViewMode={(id, days) => {
          setViewModeId(id);
          // Multi-day views always start on today so the current day is
          // never in the middle of the visible window.
          if (days > 1) {
            setAnchorDate(DateTime.now().setZone(HOME_TZ).startOf('day'));
          }
        }}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToday}
      />

      <main className="wv2-main">
        <section className={`wv2-cityblock${reorderActive ? ' wv2-cityblock--reorder' : ''}`}>
          {reorderActive && (
            <div className="wv2-cityblock-eyebrow">
              <span>{t('schedule.dragToReorder')}</span>
              <span className="wv2-cityblock-controls">
                <button className="wv2-eyebrow-pill wv2-eyebrow-pill--primary" onClick={finishReorder}>
                  {t('buttons.done')}
                </button>
              </span>
            </div>
          )}
          <div className="wv2-cityblock-rows">
            {orderedCityRows.length === 0 && (
              <div className="wv-row" style={{ padding: '12px', color: 'var(--wv-text-muted)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="wv2-add-timezone-btn"
                  onClick={() => {
                    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true }));
                  }}
                >
                  {t('buttons.addTimezone')} <kbd>⌘K</kbd>
                </button>
                <span>{t('empty.addTimezonePrompt')}</span>
              </div>
            )}
            {orderedCityRows.map(city => {
              const slots = buildHourList(dayStartHomeRef, city.timezone, use24HourFormat);
              const canDrag = reorderActive && !city.isHome;
              return (
                <CityRow
                  key={city.name + city.timezone}
                  city={city}
                  slots={slots}
                  hoveredHour={hoveredHour}
                  onHoverHour={setHoveredHour}
                  nowHour={isReferenceToday ? currentHomeHour : null}
                  onSetHome={setHomeCity}
                  onUnsetHome={() => setHomeCity(null)}
                  onRemove={onRemoveCity}
                  onStartReorder={onReorderCities ? startReorder : undefined}
                  reorderActive={canDrag}
                  onDragStart={canDrag ? handleRowDragStart(city.name) : undefined}
                  onDragOver={canDrag ? handleRowDragOver(city.name) : undefined}
                  onDragEnd={canDrag ? handleRowDragEnd : undefined}
                  onSlotClick={slotActions.handleSlotClick}
                />
              );
            })}
          </div>
        </section>

        <div className="wv2-day-stack">
          {days.map(date => {
            const iso = date.toISODate();
            const dayResult = dayEventData[iso] || {};
            return (
              <DayCard
                key={iso}
                date={date}
                isToday={date.hasSame(today, 'day')}
                currentHomeHour={currentHomeHour}
                hoveredHour={hoveredHour}
                onHoverHour={setHoveredHour}
                homeTz={HOME_TZ}
                calendars={calendars}
                eventsByCalId={eventsByDayAndCal[iso] || {}}
                allDayEvents={allDayByDay[iso] || []}
                eventsLoading={!!dayResult.loading}
                eventsError={dayResult.error}
                onPillHover={setPillTip}
                onPillClick={summary.openAndScrollToEvent}
                hoveredEventNumber={summary.hoveredEventNumber}
                clickedEventNumber={summary.clickedEventNumber}
                isAuthenticated={isAuthenticated}
                gcalConnected={gcalConnected}
                isSummarySelected={iso === summary.effectiveSummaryISO}
                onSelectForSummary={dayCount > 1 ? summary.setSummaryDateISO : undefined}
                onSlotClick={slotActions.handleSlotClick}
                meetingPickHours={meetingPickByDate[iso]}
                onOpenCalendarPreferences={onOpenCalendarPreferences}
                use24HourFormat={use24HourFormat}
              />
            );
          })}
        </div>
      </main>

      <MeetingOptionsPanel meeting={meeting} />

      <div className="wv2-bottom-bar">
        <div className="wv-status-legend wv-status-legend--bottom" aria-label={t('legend.statusAriaLabel')}>
          <span className="wv-status-legend-label">{t('legend.status')}</span>
          <span className="wv-status-legend-item">
            <span className="wv-status-swatch wv-event-pill wv-event-pill--accepted wv-event-pill--circle" style={{ '--wv-event-color': 'var(--wv-accent)' }} />
            {t('legend.accepted')}
          </span>
          <span className="wv-status-legend-item">
            <span className="wv-status-swatch wv-event-pill wv-event-pill--tentative wv-event-pill--circle" style={{ '--wv-event-color': 'var(--wv-accent)' }} />
            {t('legend.tentative')}
          </span>
          <span className="wv-status-legend-item">
            <span className="wv-status-swatch wv-event-pill wv-event-pill--declined wv-event-pill--circle" style={{ '--wv-event-color': 'var(--wv-accent)' }} />
            {t('legend.declined')}
          </span>
        </div>
        {allEventsForSummary.length > 0 && !summary.showDailySummary && (
          <button className="daily-summary-button" onClick={summary.handleOpen}>
            {t('buttons.dailySummary')}
          </button>
        )}
      </div>

      {allEventsForSummary.length > 0 && summary.showDailySummary && (
        <DailySummary
          events={allEventsForSummary}
          hasMultipleCalendars={hasMultipleCalendars}
          hoveredEventNumber={summary.hoveredEventNumber}
          setHoveredEventNumber={summary.setHoveredEventNumber}
          clickedEventNumber={summary.clickedEventNumber}
          selectedDate={summaryDate}
          use24HourFormat={use24HourFormat}
          onClose={summary.handleClose}
        />
      )}

      <footer className="app-footer">
        <p>{t('app.footer')}</p>
        <div className="footer-links">
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">{t('app.terms')}</a>
          <span className="separator">•</span>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">{t('app.privacy')}</a>
        </div>
        <p className="copyright">
          © 2025-2026 <a href="https://www.recodelabs.org" target="_blank" rel="noopener noreferrer">Recode Labs</a>. All rights reserved.
        </p>
      </footer>

      <EventTooltip tip={pillTip} />

      <ActionModal
        open={slotActions.showActionModal}
        slot={slotActions.actionSlot}
        position={slotActions.actionModalPos}
        onAddMeetingOption={slotActions.handleAddMeetingOption}
        onCopy={slotActions.handleCopyAction}
        onGmail={slotActions.handleGmailAction}
        onGCal={slotActions.handleGCalAction}
        onICal={slotActions.handleICalAction}
      />
    </div>
  );
}
